import { createTheme } from '@mui/material/styles';

/** Brand tokens — do not change primary/secondary/background defaults. */
export const brand = {
  primary: '#de5824',
  primarySoft: 'rgba(222, 88, 36, 0.12)',
  primaryHover: '#c44c1e',
  secondary: '#0f172a',
  bg: '#f4f6fb',
  paper: '#ffffff',
  border: '#e2e8f0',
  muted: '#64748b',
  success: '#059669',
  warning: '#d97706',
  info: '#2563eb',
  error: '#dc2626'
};

/** Status chip colors for registrations / leads (keep labels unchanged). */
export const statusChipColors = {
  converted: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  confirmed: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  fully_paid: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  advanced_paid: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  advance_paid: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  pending: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  on_hold: { bg: '#fef3c7', color: '#92400e', text: '#92400e', border: '#fde68a' },
  partially_booked: { bg: '#e0e7ff', color: '#3730a3', text: '#3730a3', border: '#c7d2fe' },
  advance_pending: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  remaining_pending: { bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  refunded: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  cancellation_request: { bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  follow_up_done: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  second_follow_up_done: { bg: '#ccfbf1', color: '#115e59', border: '#99f6e4' },
  third_follow_up_done: { bg: '#cffafe', color: '#155e75', border: '#a5f3fc' },
  not_interested: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  call_back_required: { bg: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  no_response: { bg: '#e2e8f0', color: '#334155', border: '#cbd5e1' },
  ringing: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  accepted: { bg: '#e0e7ff', color: '#3730a3', border: '#c7d2fe' },
  contacted: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  closed: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  open: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  in_progress: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  volunteer: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  instructor: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  camp_leader: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  photographer: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  blogger: { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  rejected: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  rusticated: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  upcoming_registration: { bg: 'rgba(222, 88, 36, 0.12)', color: '#c44c1e', border: 'rgba(222, 88, 36, 0.28)' },
  callback_request: { bg: '#f1f5f9', color: '#334155', border: '#e2e8f0' },
  pdf_request: { bg: '#f1f5f9', color: '#334155', border: '#e2e8f0' },
  incoming_call: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  office_visit: { bg: '#f1f5f9', color: '#334155', border: '#e2e8f0' },
  active: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  inactive: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  draft: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  create: { bg: '#dcfce7', color: '#065f46', border: '#a7f3d0' },
  update: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  delete: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
  applied: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  interview: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  hired: { bg: '#dcfce7', color: '#065f46', border: '#a7f3d0' },
  default: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
};

const theme = createTheme({
  palette: {
    primary: {
      main: brand.primary,
      dark: brand.primaryHover,
      light: '#f07845',
      contrastText: '#ffffff'
    },
    secondary: {
      main: brand.secondary,
      contrastText: '#ffffff'
    },
    background: {
      default: brand.bg,
      paper: brand.paper
    },
    text: {
      primary: '#0f172a',
      secondary: brand.muted
    },
    divider: brand.border,
    success: { main: brand.success },
    warning: { main: brand.warning },
    info: { main: brand.info },
    error: { main: brand.error }
  },
  shape: {
    borderRadius: 12
  },
  typography: {
    fontFamily: "'Google Sans', 'Inter', system-ui, -apple-system, sans-serif",
    h5: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.bg
        },
        '::-webkit-scrollbar': { width: 8, height: 8 },
        '::-webkit-scrollbar-thumb': {
          background: '#cbd5e1',
          borderRadius: 999
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${brand.border}`,
          boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundImage: 'none'
        },
        outlined: {
          borderColor: brand.border
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          paddingInline: 16
        },
        containedPrimary: {
          boxShadow: '0 4px 14px rgba(222, 88, 36, 0.28)',
          '&:hover': {
            boxShadow: '0 6px 18px rgba(222, 88, 36, 0.35)'
          }
        },
        outlined: {
          borderColor: brand.border,
          backgroundColor: '#fff'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined'
      }
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: '#334155',
            backgroundColor: '#f8fafc',
            borderBottom: `1px solid ${brand.border}`,
            whiteSpace: 'nowrap'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: brand.border
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.02)'
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${brand.border}`,
          backgroundImage: 'none'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: 12
        }
      }
    }
  }
});

export default theme;
